import type { CSSProperties } from "react";
import { useMemo } from "react";

type BentoItem = {
  title: string;
  description: string;
  label: string;
  size?: "small" | "big";
  className?: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function BentoCard({ item }: { item: BentoItem }) {
  const isBig = item.size === "big";
  return (
    <div
      className={[
        "group relative overflow-hidden rounded-2xl border border-border/60 bg-card/40",
        isBig ? "p-6 lg:p-7" : "p-5",
        isBig ? "min-h-[220px] lg:min-h-[260px]" : "min-h-[180px] lg:min-h-[200px]",
        "backdrop-blur supports-[backdrop-filter]:bg-card/30",
        "transition-colors",
        // spotlight & glow
        "before:pointer-events-none before:absolute before:inset-0 before:opacity-0 before:transition-opacity before:duration-300",
        "before:bg-[radial-gradient(520px_circle_at_var(--mx)_var(--my),rgba(0,0,0,0.10),transparent_55%)]",
        "dark:before:bg-[radial-gradient(520px_circle_at_var(--mx)_var(--my),rgba(255,255,255,0.12),transparent_55%)]",
        "after:pointer-events-none after:absolute after:inset-0 after:opacity-0 after:transition-opacity after:duration-300",
        "after:bg-[radial-gradient(520px_circle_at_var(--mx)_var(--my),rgba(0,0,0,0.06),transparent_60%)]",
        "dark:after:bg-[radial-gradient(520px_circle_at_var(--mx)_var(--my),rgba(255,255,255,0.06),transparent_60%)]",
        "hover:before:opacity-100 hover:after:opacity-100",
        item.className ?? "",
      ].join(" ")}
      style={
        {
          // defaults so gradient doesn't break before first move
          ["--mx" as any]: "50%",
          ["--my" as any]: "50%",
        } as CSSProperties
      }
      onPointerMove={(e) => {
        const el = e.currentTarget;
        const rect = el.getBoundingClientRect();
        const x = clamp(e.clientX - rect.left, 0, rect.width);
        const y = clamp(e.clientY - rect.top, 0, rect.height);
        el.style.setProperty("--mx", `${x}px`);
        el.style.setProperty("--my", `${y}px`);
      }}
      onPointerLeave={(e) => {
        const el = e.currentTarget;
        el.style.setProperty("--mx", "50%");
        el.style.setProperty("--my", "50%");
      }}
    >
      <div
        className={[
          "absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300",
          "group-hover:opacity-100",
          "bg-[linear-gradient(135deg,rgba(0,0,0,0.0)_0%,rgba(0,0,0,0.06)_35%,rgba(0,0,0,0.0)_70%)]",
          "dark:bg-[linear-gradient(135deg,rgba(255,255,255,0.0)_0%,rgba(255,255,255,0.06)_35%,rgba(255,255,255,0.0)_70%)]",
        ].join(" ")}
      />
      <div className="relative flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-medium tracking-wide text-muted-foreground">
              {item.label}
            </div>
            <div
              className={[
                "mt-2 font-medium leading-snug text-foreground",
                isBig ? "text-lg md:text-xl" : "text-base",
              ].join(" ")}
            >
              {item.title}
            </div>
          </div>
        </div>
        <p
          className={[
            "mt-3 leading-relaxed text-muted-foreground",
            isBig ? "text-sm md:text-base" : "text-sm",
            isBig ? "max-w-[36ch]" : "",
          ].join(" ")}
        >
          {item.description}
        </p>
        <div className="mt-auto pt-2" />
      </div>
      <div
        className={[
          "pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300",
          "group-hover:opacity-100",
          "shadow-[0_0_0_1px_rgba(0,0,0,0.12),0_0_30px_rgba(0,0,0,0.06)]",
          "dark:shadow-[0_0_0_1px_rgba(255,255,255,0.16),0_0_30px_rgba(255,255,255,0.08)]",
        ].join(" ")}
      />
    </div>
  );
}

export function MbscHeroDiagram() {
  const items = useMemo<BentoItem[]>(
    () => [
      {
        label: "Security",
        title: "2FA / KYC",
        description: "権限・本人確認・運用コントロール",
        size: "small",
        className: "lg:col-span-3",
      },
      {
        label: "Notification",
        title: "Webhook",
        description: "イベント通知でリアルタイム連携",
        size: "small",
        className: "lg:col-span-3",
      },
      {
        label: "Frontend",
        title: "MBSC React SDK",
        description: "フロントエンドから安全に統合できる開発体験。",
        size: "big",
        className: "lg:col-span-6",
      },
      {
        label: "Backend",
        title: "MBSC API",
        description: "入出金・残高・ステータスを一貫した設計で提供。",
        size: "big",
        className: "lg:col-span-6",
      },
      {
        label: "Multichain",
        title: "EVM chains supports",
        description: "Ethereum, Polygon, Arcを含めたEVMチェーン対応",
        size: "small",
        className: "lg:col-span-3",
      },
      {
        label: "Tx, User management",
        title: "Admin Dashboard",
        description: "運用者向けの可視化・管理機能",
        size: "small",
        className: "lg:col-span-3",
      },
    ],
    [],
  );

  return (
    <div className="relative">
      <div className="pointer-events-none absolute -inset-8 -z-10 rounded-[32px] bg-[radial-gradient(900px_circle_at_20%_20%,rgba(0,0,0,0.08),transparent_55%)] dark:bg-[radial-gradient(900px_circle_at_20%_20%,rgba(255,255,255,0.08),transparent_55%)]" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">
        {items.map((item) => (
          <BentoCard key={item.title} item={item} />
        ))}
      </div>
    </div>
  );
}

